import React from 'react';
import { PublicSection, Reveal } from '@/components/public';

const STAGES = [
  {
    stage: 'Discovery',
    title: "Let's Learn About Your Business",
    description:
      "Every partnership starts with a conversation. We'll learn about your business, your products or services, how you currently operate, and what you're looking to achieve—so we can understand whether Hungry Canadian is the right fit and how best to support your goals.",
  },
  {
    stage: 'Store Preparation',
    title: 'We Prepare Your Store',
    description:
      "Once you're ready to move forward, we'll prepare your online storefront—business profile, menu or product catalogue, store hours, pickup and delivery configuration, photos and branding, merchant dashboard access, and initial testing.",
  },
  {
    stage: 'Launch',
    title: 'Launch With Confidence',
    description:
      "Before your business goes live, we'll review everything together to ensure your storefront is ready. When it's time, we'll help you launch smoothly so you can begin serving customers with confidence.",
  },
  {
    stage: 'Growth & Support',
    title: "We're With You After Launch",
    description:
      "The relationship doesn't end at launch. We're available to answer questions, provide guidance, and help you make the most of the platform as your business grows and your customers' expectations evolve.",
  },
];

const strokeStyle = {
  WebkitTextStroke: '2px hsl(var(--primary))',
  color: 'transparent',
};

/**
 * "How Partnership Works" — section 4. Vertical timeline with oversized
 * outlined numerals overlapping the content for editorial weight.
 */
export default function PartnershipProcessSection() {
  return (
    <PublicSection
      id="how-partnership-works"
      testId="join-partnership-process"
      variant="alt"
      padTop="pt-10 md:pt-14"
      eyebrow="How partnership works"
      title="A straightforward path from conversation to launch."
      description="Getting started is designed to be simple. We work directly with you to understand your business, prepare your online storefront, and support your launch so you can begin serving customers with confidence."
    >
      <div className="relative max-w-4xl">
        {/* connector line */}
        <span
          aria-hidden
          className="hidden md:block absolute left-[2.1rem] top-4 bottom-4 w-px bg-border"
        />
        <ol className="space-y-10 md:space-y-14">
          {STAGES.map((s, i) => (
            <li key={s.stage} data-testid={`stage-step-${i + 1}`}>
              <Reveal delay={i * 0.05}>
                <div className="grid md:grid-cols-[5rem_1fr] gap-4 md:gap-8">
                  <div
                    aria-hidden
                    className="text-6xl md:text-7xl font-heading font-black leading-none select-none"
                    style={strokeStyle}
                  >
                    {i + 1}
                  </div>
                  <div className="bg-background border border-border p-6 md:p-8 transition-colors hover:border-primary/50">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">
                      Step {i + 1} · {s.stage}
                    </p>
                    <h3 className="text-xl md:text-2xl font-heading font-bold tracking-tight text-foreground">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                      {s.description}
                    </p>
                  </div>
                </div>
              </Reveal>
            </li>
          ))}
        </ol>
      </div>

      <Reveal delay={0.1}>
        <div
          className="mt-14 md:mt-16 max-w-4xl bg-background border border-border border-l-4 border-l-primary px-6 py-6 md:px-9 md:py-8"
          data-testid="partnership-process-callout"
        >
          <p className="text-xl md:text-2xl font-heading font-bold tracking-tight text-foreground mb-2">
            Every business is different.
          </p>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Whether you&apos;re launching your first online ordering system or
            adding another sales channel to an established business, we&apos;ll
            tailor the onboarding process to fit your operation.
          </p>
        </div>
      </Reveal>
    </PublicSection>
  );
}
